// ═══════════════════════════════════════════════════════════════════════════
// Supabase auth shim — ported from the real Auth.tsx override (no SDK,
// direct REST calls). Session is stored under the SAME key the live app
// uses ("payvit-auth-token"), so the session shape this produces is exactly
// what TransfeFlow.tsx's trySession() expects to read.
// ═══════════════════════════════════════════════════════════════════════════

const SUPABASE_URL = process.env.SUPABASE_URL as string
const SUPABASE_ANON_KEY = process.env.SUPABASE_ANON_KEY as string

export const AUTH_STORAGE_KEY = "payvit-auth-token"
const REMEMBER_KEY = "payvit-remember-device"

export interface SupabaseSession {
    access_token: string
    refresh_token: string
    expires_at: number
    expires_in?: number
    token_type?: string
    user?: { id: string; email: string; user_metadata?: { full_name?: string; name?: string } }
}

// ─── Remember-device: which storage the session actually lives in ──────────
// The preference itself always lives in localStorage (it has to survive a
// closed tab to mean anything). The session payload goes to localStorage
// when remembered, sessionStorage when not — cleared the moment the tab
// closes, same as a bank app that doesn't trust the device.
export function getRememberDevice(): boolean {
    try {
        const v = localStorage.getItem(REMEMBER_KEY)
        return v === null ? true : v === "1"
    } catch (_e) {
        return true
    }
}
function setRememberDeviceRaw(v: boolean) {
    try { localStorage.setItem(REMEMBER_KEY, v ? "1" : "0") } catch (_e) {}
}
function activeStorage(): Storage {
    return getRememberDevice() ? localStorage : sessionStorage
}

const _read = (): SupabaseSession | null => {
    try {
        return JSON.parse(activeStorage().getItem(AUTH_STORAGE_KEY) ?? "null")
    } catch (_e) {
        return null
    }
}
const _save = (s: SupabaseSession) => {
    try {
        activeStorage().setItem(AUTH_STORAGE_KEY, JSON.stringify(s))
    } catch (_e) {}
}
const _clear = () => {
    try {
        localStorage.removeItem(AUTH_STORAGE_KEY)
        sessionStorage.removeItem(AUTH_STORAGE_KEY)
    } catch (_e) {}
}
const _hdr = (token?: string): Record<string, string> => ({
    "Content-Type": "application/json",
    apikey: SUPABASE_ANON_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
})
const _post = async (path: string, body: any, token?: string) => {
    const r = await fetch(`${SUPABASE_URL}${path}`, {
        method: "POST",
        headers: _hdr(token),
        body: JSON.stringify(body),
    })
    return { ok: r.ok, data: await r.json() }
}

/** Decodes id/email/full_name straight out of the JWT's own claims — no
 *  network round trip. Ported from the real Auth.tsx's decodeJwtUser. Used
 *  both by the hash-based recovery/signup-confirmation flow (the redirect
 *  hash never carries a user object, only the raw tokens) and to fill in a
 *  display name anywhere one's needed. */
export function decodeJwtUser(token: string): { id: string; email: string; name: string | null } | null {
    try {
        const payload = token.split(".")[1]
        const json = decodeURIComponent(
            atob(payload.replace(/-/g, "+").replace(/_/g, "/"))
                .split("")
                .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
                .join("")
        )
        const claims = JSON.parse(json)
        const meta = claims.user_metadata ?? {}
        const name = meta.full_name ?? meta.name ?? null
        return { id: claims.sub ?? "", email: claims.email ?? "", name: typeof name === "string" ? name : null }
    } catch (_e) {
        return null
    }
}

/** Maps Supabase's raw English error strings to the same friendly Kreyòl
 *  messages the real Auth.tsx shows — ported verbatim (same substrings,
 *  same fallback). A user should never see a raw "invalid login
 *  credentials" or a JSON blob. */
export function translateError(msg: string): string {
    const m = (msg || "").toLowerCase()
    if (m.includes("invalid login credentials") || m.includes("invalid email or password"))
        return "Imèl oswa modpas ou a pa kòrèk."
    if (m.includes("email not confirmed"))
        return "Tanpri konfime imèl ou anvan ou konekte."
    if (m.includes("user already registered") || m.includes("already been registered"))
        return "Yon kont deja egziste ak imèl sa."
    if (m.includes("password should be at least"))
        return "Modpas dwe gen omwen 6 karaktè."
    if (m.includes("email rate limit exceeded") || m.includes("too many requests"))
        return "Twòp eseyi. Tanpri tann yon ti moman epi eseye ankò."
    if (m.includes("network") || m.includes("fetch"))
        return "Pwoblèm koneksyon. Verifye entènèt ou epi eseye ankò."
    return "Yon erè te fèt. Tanpri eseye ankò."
}

function isExpired(s: SupabaseSession) {
    return Date.now() / 1000 > (s.expires_at ?? 0) - 60
}

/** Reads the stored session, transparently refreshing it if it's within 60s
 *  of expiry (or already expired but the refresh token is still good).
 *  Returns null if there's no session or the refresh fails. */
export async function getSession(): Promise<SupabaseSession | null> {
    const s = _read()
    if (!s) return null
    if (!isExpired(s)) return s
    if (!s.refresh_token) {
        _clear()
        return null
    }
    try {
        const { ok, data } = await _post("/auth/v1/token?grant_type=refresh_token", {
            refresh_token: s.refresh_token,
        })
        if (ok) {
            const merged = { ...data, user: data.user ?? s.user }
            _save(merged)
            return merged
        }
    } catch (_e) {}
    _clear()
    return null
}

/** `remember` controls which storage the session lands in for THIS sign-in —
 *  set before saving, since the response has to be written straight to the
 *  storage the user actually chose. */
export async function signInWithPassword(
    email: string,
    password: string,
    remember: boolean = true
): Promise<{ session: SupabaseSession | null; error: string | null }> {
    try {
        const { ok, data } = await _post("/auth/v1/token?grant_type=password", { email, password })
        if (!ok) {
            return { session: null, error: translateError(data.error_description ?? data.msg ?? "") }
        }
        setRememberDeviceRaw(remember)
        _save(data)
        return { session: data, error: null }
    } catch (e: any) {
        return { session: null, error: translateError(e?.message ?? "network") }
    }
}

export async function signUp(
    email: string,
    password: string,
    emailRedirectTo?: string
): Promise<{ error: string | null }> {
    try {
        const body: any = { email, password }
        if (emailRedirectTo) body.email_redirect_to = emailRedirectTo
        const { ok, data } = await _post("/auth/v1/signup", body)
        if (!ok) return { error: translateError(data.error_description ?? data.msg ?? "") }
        // A project with email confirmation OFF returns a session directly —
        // save it so signup doubles as an immediate login in that case.
        if (data?.access_token) _save(data)
        return { error: null }
    } catch (e: any) {
        return { error: translateError(e?.message ?? "network") }
    }
}

export async function resetPasswordForEmail(
    email: string,
    redirectTo?: string
): Promise<{ error: string | null }> {
    try {
        const body: any = { email }
        if (redirectTo) body.redirect_to = redirectTo
        const { ok, data } = await _post("/auth/v1/recover", body)
        if (!ok) return { error: translateError(data.error_description ?? "") }
        return { error: null }
    } catch (e: any) {
        return { error: translateError(e?.message ?? "network") }
    }
}

/** Completes a recovery flow — sets a new password on the session captured
 *  from the recovery-link hash (see `captureSessionFromUrlHash`). */
export async function updateUserPassword(newPassword: string): Promise<{ error: string | null }> {
    const s = _read()
    if (!s?.access_token) return { error: "Non authentifié." }
    try {
        const r = await fetch(`${SUPABASE_URL}/auth/v1/user`, {
            method: "PUT",
            headers: _hdr(s.access_token),
            body: JSON.stringify({ password: newPassword }),
        })
        const data = await r.json()
        if (!r.ok) return { error: translateError(data.error_description ?? "") }
        return { error: null }
    } catch (e: any) {
        return { error: translateError(e?.message ?? "network") }
    }
}

export async function signOut(): Promise<void> {
    try {
        const s = _read()
        if (s?.access_token) {
            await fetch(`${SUPABASE_URL}/auth/v1/logout`, { method: "POST", headers: _hdr(s.access_token) })
        }
    } catch (_e) {}
    _clear()
}

/** Auth links (password-recovery, signup-confirmation, email-change,
 *  magiclink) come back as `#access_token=...&refresh_token=...&type=...` —
 *  a URL fragment, never a query string, and never a user object (there's
 *  no room for one in a fragment). Call once on app mount. Returns the
 *  hash's `type` if a session was captured (so the caller can route straight
 *  to a "set new password" screen for `recovery`), or null if there was
 *  nothing to handle. Ported from the real Auth.tsx, ~verbatim. */
export function captureSessionFromUrlHash(): string | null {
    if (typeof window === "undefined") return null
    const params = new URLSearchParams(window.location.hash.slice(1))
    const access_token = params.get("access_token")
    const type = params.get("type")
    if (!access_token || (type !== "recovery" && type !== "signup" && type !== "email_change" && type !== "magiclink"))
        return null
    try {
        const jwtUser = decodeJwtUser(access_token)
        setRememberDeviceRaw(true)
        _save({
            access_token,
            refresh_token: params.get("refresh_token") ?? "",
            expires_at: Number(params.get("expires_at")) || 0,
            expires_in: Number(params.get("expires_in")) || 3600,
            token_type: params.get("token_type") ?? "bearer",
            ...(jwtUser ? { user: { id: jwtUser.id, email: jwtUser.email } } : {}),
        })
    } catch (_e) {
        return null
    }
    window.history.replaceState(null, "", window.location.pathname + window.location.search)
    return type
}

/** Fires on `storage` events so a login/logout in another tab is reflected
 *  here too — matches the real shim's cross-tab behavior. Only fires for
 *  whichever storage is currently active, same as the real app: switching
 *  "remember device" mid-session isn't a case either shim handles. */
export function onAuthStateChange(callback: (session: SupabaseSession | null) => void): () => void {
    const handler = (e: StorageEvent) => {
        if (e.key !== AUTH_STORAGE_KEY) return
        if (!e.newValue) {
            callback(null)
            return
        }
        try {
            callback(JSON.parse(e.newValue))
        } catch (_e) {}
    }
    window.addEventListener("storage", handler)
    return () => window.removeEventListener("storage", handler)
}